import { makeStyles } from 'tss-react/mui';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import React, { FC, ReactElement, useEffect, useState } from 'react';
import {
  IActiveFilterValues,
  IFilter,
  IFilterValue
} from '../Interface/Interface';
import { black, lightgray } from '../Constants/Colors';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
      borderRadius: 3
    }
  }
};

const useStyles = makeStyles()((theme) => {
  return {
    label: {
      overflow: 'visible !important',
      fontSize: '14px !important',
      lineHeight: '1em !important',

      '&.Mui-focused, &.MuiInputLabel-shrink': {
        lineHeight: '1.5em !important',
        '&:after': {
          content: '""',
          position: 'absolute',
          left: '-5px',
          right: 0,
          bottom: 0,
          height: 'calc(50% + 2px)',
          width: 'calc(100% + 10px)',
          background: lightgray,
          zIndex: '-1'
        }
      }
    },
    select: {
      borderRadius: '4px !important',
      backgroundColor: lightgray,
      height: '48px',
      '& fieldset': {
        borderColor: black,
        borderWidth: 1.5
      },
      '& svg': {
        fill: black
      }
    },
    formControl: {
      width: '25% !important',
      [theme.breakpoints.down('sm')]: {
        marginTop: '24px !important',
        width: '100% !important'
      }
    }
  };
});

interface IFilterSelectProps {
  filter: IFilter;
  onFilterChanged: (filterIdentifier: string, selectedFilters: any) => void;
  activeFilters: IActiveFilterValues;
}

const FilterSelect: FC<IFilterSelectProps> = ({
  filter,
  onFilterChanged,
  activeFilters
}): ReactElement => {
  const { classes } = useStyles();

  const [selectedFilterValues, setSelectedFilterValues] = useState<
    IFilterValue[]
  >([]);

  useEffect(() => {
    if (!selectedFilterValues) return;

    onFilterChanged(filter.identifier, selectedFilterValues);

    // eslint-disable-next-line
  }, [selectedFilterValues]);

  useEffect(() => {
    if (!activeFilters) {
      setSelectedFilterValues([]);
    } else if (activeFilters[filter.identifier]) {
      setSelectedFilterValues(activeFilters[filter.identifier]);
    }
  }, [activeFilters, filter]);

  const handleChange = (event: any) => {
    const {
      target: { value }
    } = event;

    const filterValues = filter.values.filter((filterValue) =>
      value.includes(filterValue)
    );

    setSelectedFilterValues(filterValues);
  };

  return (
    <FormControl
      className={classes.formControl}
      fullWidth
      key={filter.identifier}>
      <InputLabel id={`${filter.identifier}-label`} className={classes.label}>
        {filter.title}
      </InputLabel>
      <Select
        labelId={`${filter.identifier}-label`}
        id={`${filter.identifier}`}
        name={`${filter.identifier}`}
        multiple
        value={selectedFilterValues}
        onChange={handleChange}
        renderValue={(selected) =>
          selected ? selected.map(({ title }) => title).join(', ') : ''
        }
        className={classes.select}
        MenuProps={MenuProps}>
        {filter.values.map((filterValue) => {
          return (
            <MenuItem
              key={`${filter.identifier}-${filterValue.value}`}
              value={filterValue as any}>
              <Checkbox
                checked={selectedFilterValues.some(
                  (selected) => selected.value === filterValue.value
                )}
              />
              <ListItemText primary={filterValue.title} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default FilterSelect;
